import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Handshake, Route } from "lucide-react";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import { COMPANY, IMAGES } from "@/config/site";

const FIGURES = [
  { value: "2022", label: "Année de création" },
  { value: "3,5 – 40 T", label: "de PTAC couverts" },
  { value: "8", label: "pays desservis" },
  { value: "24 h", label: "pour répondre à toute demande" },
];

const VALUES = [
  {
    icon: ShieldCheck,
    title: "Fiabilité",
    text: "Licence communautaire, capacité professionnelle et assurance marchandises AXA France IARD : vos biens sont entre des mains déclarées et assurées.",
  },
  {
    icon: Handshake,
    title: "Proximité",
    text: "Un interlocuteur unique, du devis à la livraison. Nous répondons à toute demande sous 24 heures ouvrées.",
  },
  {
    icon: Route,
    title: "Flexibilité",
    text: "Du fourgon à la semi-remorque, de la course urgente au contrat récurrent : nous adaptons le véhicule et l'organisation à votre besoin.",
  },
];

const APropos = () => (
  <>
    <Seo
      title="À propos — ASTROLOG, transporteur routier dans l'Aube"
      description="ASTROLOG, SAS au capital de 75 000 € immatriculée au RCS de Troyes : transport routier de marchandises, location avec conducteur et déménagement depuis 2022."
      path="/a-propos"
    />

    <section className="brand-dark noise relative overflow-hidden py-20 sm:py-24">
      <div className="absolute inset-0">
        <img src={IMAGES.trucks} alt="" aria-hidden="true" className="h-full w-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-[#0A0E1A]/70" />
      </div>
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal>
          <h1 className="max-w-3xl font-heading text-4xl font-bold tracking-tighter text-[#FAFAF7] sm:text-5xl">
            ASTROLOG, la route en toute confiance
          </h1>
          <p className="mt-5 max-w-2xl text-lg text-slate-300">
            Une entreprise de transport à taille humaine, fondée en 2022 à La
            Chapelle-Saint-Luc, au service des particuliers et des professionnels.
          </p>
        </Reveal>
      </div>
    </section>

    <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8" data-testid="story-section">
      <div className="grid items-start gap-12 lg:grid-cols-2">
        <Reveal>
          <h2 className="font-heading text-3xl font-semibold tracking-tight sm:text-4xl">Notre histoire</h2>
          <div className="mt-6 space-y-4 leading-relaxed text-slate-600">
            <p>
              ASTROLOG est une Société par Actions Simplifiée au capital de{" "}
              {COMPANY.capital}, immatriculée au Registre du Commerce et des
              Sociétés de Troyes le 18 juillet 2022 (SIREN {COMPANY.siren}) et en
              activité depuis le 1er juillet 2022.
            </p>
            <p>
              Basée à La Chapelle-Saint-Luc, dans l'Aube, la société est
              spécialisée dans le transport public routier de marchandises, la
              location de véhicules industriels avec conducteurs et le
              déménagement, pour des véhicules de tous tonnages de plus de 3,5
              tonnes de PMA.
            </p>
            <p>
              Titulaire de la licence communautaire de transport routier de
              marchandises et de l'attestation de capacité professionnelle,
              ASTROLOG assure également une mission de commissionnaire de
              transport pour organiser des flux multimodaux.
            </p>
          </div>
        </Reveal>
        <Reveal delay={120}>
          <div className="rounded-3xl border border-slate-200 bg-white p-8">
            <h3 className="font-heading text-lg font-semibold">Informations légales</h3>
            <dl className="mt-5 space-y-3 text-sm">
              {[
                ["Dénomination", `${COMPANY.name} (${COMPANY.legalForm})`],
                ["Capital social", COMPANY.capital],
                ["Siège social", COMPANY.fullAddress],
                ["RCS", COMPANY.rcs],
                ["SIRET", COMPANY.siret],
                ["Code APE / NAF", COMPANY.ape],
                ["TVA intracommunautaire", COMPANY.tva],
                ["Assurance marchandises", `${COMPANY.insurer} — France + UE`],
              ].map(([k, v]) => (
                <div key={k} className="flex justify-between gap-4 border-b border-slate-100 pb-3">
                  <dt className="text-slate-500">{k}</dt>
                  <dd className="text-right font-medium text-foreground">{v}</dd>
                </div>
              ))}
            </dl>
          </div>
        </Reveal>
      </div>

      {/* Chiffres clés */}
      <div className="mt-16 grid grid-cols-2 gap-4 lg:grid-cols-4" data-testid="figures-section">
        {FIGURES.map((f, i) => (
          <Reveal key={f.label} delay={i * 80}>
            <div className="rounded-2xl border border-slate-200 bg-white p-6 text-center transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-lg">
              <p className="font-heading text-3xl font-bold text-primary">{f.value}</p>
              <p className="mt-1 text-sm text-slate-500">{f.label}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>

    {/* Valeurs */}
    <section className="brand-dark noise relative py-16 sm:py-20" data-testid="values-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal className="max-w-2xl">
          <h2 className="font-heading text-3xl font-semibold tracking-tight text-[#FAFAF7] sm:text-4xl">
            Nos engagements
          </h2>
        </Reveal>
        <div className="mt-10 grid gap-6 md:grid-cols-3">
          {VALUES.map((v, i) => (
            <Reveal key={v.title} delay={i * 100}>
              <div className="h-full rounded-2xl border border-white/10 bg-white/5 p-7">
                <v.icon className="h-8 w-8 text-secondary" strokeWidth={1.75} />
                <h3 className="mt-4 font-heading text-xl font-semibold text-[#FAFAF7]">{v.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-400">{v.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={200}>
          <div className="mt-12">
            <Link
              to="/contact"
              data-testid="about-devis-cta"
              className="inline-flex items-center gap-2 rounded-full bg-accent px-8 py-4 font-heading text-base font-semibold text-white shadow-md transition-[transform,background-color,box-shadow] duration-300 hover:-translate-y-1 hover:bg-[#EA580C] hover:shadow-xl"
            >
              Travailler avec nous <ArrowRight className="h-5 w-5" />
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  </>
);

export default APropos;
